import { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedClientId } from '../features/onboarding/onboardingSlice';

/**
 * Custom hook for client-based filtering
 * Provides the selected client ID, client info, and filtering utilities
 * 
 * @returns {Object} Client filter utilities
 */
export const useClientFilter = () => {
    const dispatch = useDispatch();
    
    // Get selected client ID and clients list from Redux
    const selectedClientId = useSelector((state) => state.onboarding.selectedClientId);
    const clients = useSelector((state) => state.onboarding.clients);
    const clientsStatus = useSelector((state) => state.onboarding.clientsStatus);

    // Get the full selected client object
    const selectedClient = useMemo(() => {
        if (!selectedClientId || !clients.length) return null;
        return clients.find(
            (client) => client.clientUUID === selectedClientId || client.id === selectedClientId
        ) || null;
    }, [selectedClientId, clients]);

    // Set the selected client
    const setSelectedClient = useCallback((clientId) => {
        dispatch(setSelectedClientId(clientId));
    }, [dispatch]);

    /**
     * Filter an array of items by clientUUID
     * @param {Array} items - Array of items to filter
     * @param {string} clientIdField - The field name containing the client UUID (default: 'clientUUID')
     * @returns {Array} Filtered items matching the selected client
     */
    const filterByClient = useCallback((items, clientIdField = 'clientUUID') => {
        if (!items || !Array.isArray(items)) return [];
        if (!selectedClientId) return items; // Return all items if no client selected
        
        return items.filter((item) => item[clientIdField] === selectedClientId);
    }, [selectedClientId]);

    /**
     * Check if an item belongs to the selected client
     * @param {Object} item - The item to check
     * @param {string} clientIdField - The field name containing the client UUID (default: 'clientUUID')
     * @returns {boolean} True if item belongs to selected client
     */
    const belongsToSelectedClient = useCallback((item, clientIdField = 'clientUUID') => {
        if (!item || !selectedClientId) return true; // No filtering if no client selected
        return item[clientIdField] === selectedClientId;
    }, [selectedClientId]);

    /**
     * Get client name by UUID
     * @param {string} clientUUID - The client UUID to look up
     * @returns {string} Client name or the UUID if not found
     */
    const getClientName = useCallback((clientUUID) => {
        if (!clientUUID || !clients.length) return clientUUID || '-';
        const client = clients.find(
            (c) => c.clientUUID === clientUUID || c.id === clientUUID
        );
        return client?.clientName || client?.name || clientUUID;
    }, [clients]);

    return {
        // State
        selectedClientId,
        selectedClient,
        clients,
        clientsStatus,
        isLoading: clientsStatus === 'loading',
        
        // Actions
        setSelectedClient,
        
        // Utilities
        filterByClient,
        belongsToSelectedClient,
        getClientName,
    };
};

export default useClientFilter;

